<template>
    <page :busy="busy" :tabs="tabs">
        <b-container fluid>
            <b-row>
                <b-col cols="12" sm="8" md="6" xl="4">
                    <div class="emarketing-setup-form d-flex flex-column py-3">
                        <form-select
                            v-model="platform"
                            floatingLabel="Platform:"
                            :options="platformChoices"
                            :leftIcon="['fas', 'envelope']"
                            @change="changedPlatform"
                        />
                        <template v-if="platform > 0">
                            <form-api-key-field
                                class="mb-3"
                                label="API Key:"
                                :leftIcon="['fas', 'key']"
                                v-model="credentials.password"
                                :disabled="false"
                                :withSpace="false"
                            />
                        </template>

                        <b-form-group v-if="platform > 0 && lists.length == 0 && workingCredentials && displayWarnings">
                            <label
                                >No lists found. Please create a mailing list within MailChimp website and refresh this
                                page.</label
                            >
                        </b-form-group>
                        <b-form-group
                            v-if="platform > 0 && lists.length == 0 && !workingCredentials && displayWarnings"
                        >
                            <label>Invalid Credentials. Please check your API Key.</label>
                        </b-form-group>

                        <template v-if="platform > 0 && lists.length > 0">
                            <form-select
                                v-model="list"
                                floatingLabel="Audience Name:"
                                :options="lists"
                                :leftIcon="['fas', 'list']"
                            />
                        </template>

                        <template v-if="showProgressBar">
                            <b-progress height="2rem" :class="{ 'mb-3': progress === 100 }">
                                <b-progress-bar :value="progress"> {{ progress }}% </b-progress-bar>
                            </b-progress>
                            <b-form-group v-if="progress < 100">
                                <label class="mt-1">Synchronizing guests. Please wait ...</label>
                            </b-form-group>
                        </template>

                        <div class="d-flex flex-wrap">
                            <b-button
                                class="mr-1 mb-1"
                                variant="outline-secondary"
                                :disabled="showProgressBar && progress !== 100"
                                @click="savePlatform(false)"
                            >
                                Save Platfrom
                            </b-button>
                            <b-button
                                class="mr-2 mb-1"
                                v-if="platform > 0 && lists.length > 0"
                                variant="outline-secondary"
                                :disabled="(showProgressBar && progress !== 100) || !list"
                                @click="syncGuests"
                            >
                                Sync Guests
                            </b-button>
                            <b-button
                                class="mb-1"
                                v-if="platform > 0 && lists.length > 0"
                                variant="danger"
                                :disabled="(showProgressBar && progress !== 100) || !list"
                                @click="disconnect"
                            >
                                Disconnect
                            </b-button>
                        </div>
                    </div>
                    <div class="email-guest-section">
                        <h4 class="mt-1">Email All Guests</h4>
                        <div class="mailchimp-info">
                            <a href="https://mailchimp.com/" target="_blank">
                                <img
                                    src="/static/images/mailchimp-light.png"
                                    alt="Mailchimp Logo"
                                    class="mailchimp-logo"
                                />
                            </a>
                            <div class="mailchimp-description">
                                <p>
                                    Lodgix has partnered with Mailchimp to dynamically sync all guest contact data with
                                    Mailchimp. Mailchimp is a powerful marketing platform designed to streamline your
                                    guest communications and engagement. With its user-friendly interface, Mailchimp
                                    enables you to create and manage email campaigns, automate workflows, and analyze
                                    performance metrics—all in one place.
                                </p>
                                <p>
                                    By leveraging its extensive tools for audience segmentation and personalized
                                    messaging, your business can connect more effectively with your guests to foster
                                    lasting relationships and many repeat guests!
                                </p>
                            </div>
                            <div>
                                <b-button
                                    variant="primary"
                                    target="_blank"
                                    href="https://support.lodgix.com/en/articles/3323782-sending-emails-to-all-guests-using-mailchimp"
                                >
                                    Learn More
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import FormApiKeyField from '../generic/form-api-key-field/form-api-key-field.vue';
    import { WebSocketClient } from '../../websocket/client';
    import {
        EMARKETING_GET_DATA,
        EMARKETING_SAVE_PLATFORM,
        EMARKETING_SYNC_PLATFORM,
        EMARKETING_GET_LISTS,
        EMARKETING_DISCONNECT_PLATFORM,
    } from '../../endpoints';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';

    const PLATFORM_NOT_SET = -1;

    export default {
        name: 'EmailMarketingSetup',
        components: {
            Page,
            FormInput,
            FormSelect,
            ResponsiveButton,
            FormApiKeyField,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                busy: false,
                tabs: [
                    {
                        title: 'Email Marketing Setup',
                        route: '/app/emarketing/setup',
                        active: true,
                    },
                ],
                websocketClient: new WebSocketClient(),
                progress: 0,
                workingCredentials: false,
                displayWarnings: false,
                platformChoices: [],
                credentials: { password: '' },
                platform: null,
                platformLabel: null,
                list: null,
                lists: [],
                showProgressBar: false,
            };
        },
        computed: {
            ...mapGetters('header', ['userWebsocketToken']),
        },
        async mounted() {
            this.showProgressBar = false;
            this.progress = 0;
            await this.fetchData();
            if (this.platform > 0 && this.list && this.credentials.password) {
                this.savePlatform(true);
            }
        },
        methods: {
            handleWebsocketMessage(message) {
                this.progress = this.getProgress(message.msg);
            },
            getProgress(str) {
                const regexp = /(PROGRESS),(\d+)/i;
                const result = str && str.match(regexp);
                return (result && Number(result[2])) || null;
            },
            async fetchData() {
                try {
                    this.busy = true;
                    this.showProgressBar = false;
                    const { data } = await API.get(EMARKETING_GET_DATA());
                    const { subscriber_platform: subscriberPlatform, platform_choices: platformChoices } = data;
                    this.platformChoices = platformChoices.map((choice) => {
                        return {
                            text: choice.name,
                            value: choice.id,
                        };
                    });
                    this.progress = 0;
                    this.lists = [];
                    this.platform = subscriberPlatform.platform || PLATFORM_NOT_SET;
                    this.list = subscriberPlatform.list_id;
                    this.credentials = subscriberPlatform.credentials || { password: '' };
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch platform choices' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchList() {
                try {
                    this.displayWarnings = false;
                    const { data } = await API.get(EMARKETING_GET_LISTS());
                    this.lists = data.lists.map((list) => ({
                        value: list.id,
                        text: list.name,
                    }));
                    if (!this.list && this.lists.length > 0) {
                        this.list = this.lists[0].id;
                    }
                    this.workingCredentials = data.working_credentials;
                    this.displayWarnings = true;
                    if (!this.workingCredentials || this.lists.length === 0) {
                        this.showProgressBar = false;
                        this.progress = 0;
                    }
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch list' });
                }
            },
            async savePlatform(isStart = false) {
                try {
                    this.busy = true;
                    await API.post(EMARKETING_SAVE_PLATFORM(), {
                        platform: this.platform === -1 ? null : this.platform,
                        credentials: this.credentials,
                        list_id: this.list,
                        token: this.userWebsocketToken,
                    });
                    await this.fetchList();
                    if (!isStart) {
                        this.successToast({ text: 'Email Marketing settings successfully saved.' });
                    }
                } catch (e) {
                    this.errorToast({ text: 'Could not save platform' });
                } finally {
                    this.busy = false;
                }
            },
            async syncGuests() {
                try {
                    this.showProgressBar = true;
                    const { data } = await API.post(EMARKETING_SYNC_PLATFORM(), {
                        platform: this.platform || PLATFORM_NOT_SET,
                        credentials: this.credentials,
                        list_id: this.list,
                        token: this.userWebsocketToken,
                    });
                    if (data.status == 'OK') {
                        this.progress = 5;
                    } else {
                        this.progress = 0;
                    }
                    await this.fetchList();
                } catch (e) {
                    const { response } = e;
                    this.showProgressBar = false;
                    if (response.status == 400 && response.data?.errors) {
                        this.errorToast({ text: response.data.errors });
                    } else {
                        this.errorToast({ text: 'Could not synchronizing guests' });
                    }
                }
            },
            async disconnect() {
                try {
                    this.busy = true;
                    await API.post(EMARKETING_DISCONNECT_PLATFORM(), {
                        platform: this.platform,
                        credentials: this.credentials,
                        list_id: this.list,
                    });
                    this.platform = null;
                    this.credentials = { password: '' };
                    this.list = null;
                    this.lists = [];
                    await this.fetchData();
                    this.successToast({ text: 'Disconnected.' });
                } catch (e) {
                    this.errorToast({ text: 'Could not disconnect' });
                } finally {
                    this.busy = false;
                    this.showProgressBar = false;
                }
            },
            changedPlatform() {
                this.displayWarnings = false;
            },
        },
        watch: {
            userWebsocketToken(newVal, oldVal) {
                this.websocketClient.initialize({ token: this.userWebsocketToken });
                this.websocketClient.setMessageCallback(this.handleWebsocketMessage);
            },
        },
    };
</script>

<style lang="css" scoped>
    .email-guest-section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 20px;
        background-color: #f9f9f9; /* Light background */
        border-radius: 8px; /* Rounded corners */
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Subtle shadow */
    }

    .mailchimp-info {
        display: flex;
        flex-direction: column; /* Stack elements vertically */
        align-items: flex-start; /* Align items to start */
        margin-top: 10px; /* Space between title and logo/text */
    }

    .mailchimp-logo {
        width: 100px; /* Adjust size */
        height: auto; /* Maintain aspect ratio */
        margin-bottom: 15px; /* Space below the logo */
        border-radius: 5px;
    }

    .mailchimp-description {
        margin-bottom: 15px; /* Space between description and link */
    }
</style>
